import { useCallback, useState } from 'react';
import { callBackend } from '../../../utils/api';
import { useAppContext } from '../../../context/AppContext';
import { normalizeUserFactorModel } from '../../../utils/factorDataOptions';
import {
    buildFactorContextFromMatrixMetadata,
    flattenFactorGroups,
    isAiIdioVolSource,
    isExpectedNoWatchlistMatrixError,
    normalizeIdioVolSource,
} from '../dashboardUtils';

const CLOUD_RUN_ERROR_PATTERN = /Cloud Run call failed:\s*(\d+)\s*-\s*(.*)$/;
const PARTIAL_IDENTITY_CODES = new Set(['identity_partial', 'identity_unresolved_all']);
const LEGACY_IDENTITY_CODES = new Set(['ambiguous_identifier', 'unresolved_identifier']);
const SUCCESS_STATUS = 'SUCCESS';
const IDENTITY_PARTIAL_STATUS = 'IDENTITY_PARTIAL';
const IDENTITY_UNRESOLVED_ALL_STATUS = 'IDENTITY_UNRESOLVED_ALL';
const WATCHLIST_MATRIX_PROBE_STATES = Object.freeze({
    READY: 'ready',
    EMPTY: 'empty',
    PENDING: 'pending',
    UNAVAILABLE: 'unavailable',
});
const SERVING_MANIFEST_CONTEXT_UNAVAILABLE = 'serving_manifest_context_unavailable';
const WATCHLIST_FACTOR_SERVING_DATA_DRIFT = 'watchlist_factor_serving_data_drift';
// get_factor_matrices answers this when no Watchlist security is risk-eligible.
const NOTHING_ELIGIBLE_STATUS = 'nothing_eligible';

const formatMatrixLoadResult = ({
    response = null,
    matrixLoadStatus = 'unavailable',
    matrixLoadReason = null,
} = {}) => ({
    response,
    matrixLoadStatus,
    matrixLoadReason,
});

const normalizeText = (value) => {
    if (value == null) {
        return '';
    }
    return String(value).trim();
};

const normalizeCount = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
        return null;
    }
    return parsed;
};

const normalizeStartupIdentityStatus = ({
    status = '',
    identityResolution = null,
    hasResolvedRows = false,
} = {}) => {
    const normalizedStatus = normalizeText(status).toUpperCase();
    if (
        normalizedStatus === SUCCESS_STATUS
        || normalizedStatus === IDENTITY_PARTIAL_STATUS
        || normalizedStatus === IDENTITY_UNRESOLVED_ALL_STATUS
    ) {
        return normalizedStatus;
    }
    if (normalizedStatus) {
        return normalizedStatus;
    }

    const resolutionStatus = normalizeText(identityResolution?.status).toLowerCase();
    const resolutionCode = normalizeText(identityResolution?.code).toLowerCase();
    if (resolutionStatus === 'partial' || PARTIAL_IDENTITY_CODES.has(resolutionCode)) {
        return IDENTITY_PARTIAL_STATUS;
    }
    if (LEGACY_IDENTITY_CODES.has(resolutionCode)) {
        return hasResolvedRows ? IDENTITY_PARTIAL_STATUS : IDENTITY_UNRESOLVED_ALL_STATUS;
    }

    return hasResolvedRows ? SUCCESS_STATUS : IDENTITY_UNRESOLVED_ALL_STATUS;
};

const isStartupIdentityNonFatal = ({
    status = '',
    identityResolution = null,
    hasResolvedRows = false,
} = {}) => {
    const normalizedStatus = normalizeStartupIdentityStatus({
        status,
        identityResolution,
        hasResolvedRows,
    });
    return (
        normalizedStatus === IDENTITY_PARTIAL_STATUS
        || normalizedStatus === IDENTITY_UNRESOLVED_ALL_STATUS
    );
};

const countActionableOutboundAmbiguities = (payload) => {
    if (!payload || typeof payload !== 'object') {
        return 0;
    }

    const explicitCount = normalizeCount(payload?.actionable_error_count);
    if (explicitCount != null) {
        return explicitCount;
    }

    const errors = Array.isArray(payload?.errors) ? payload.errors : [];
    return errors.filter((item) => {
        const code = normalizeText(item?.code).toLowerCase();
        if (code !== 'ambiguous_identifier') {
            return false;
        }
        const candidates = Array.isArray(item?.candidates) ? item.candidates : [];
        return candidates.some((candidate) => normalizeText(candidate?.barrid));
    }).length;
};

const parseCloudRunError = (error) => {
    if (error?.name === 'BackendHttpError') {
        return {
            statusCode: error.status,
            code: normalizeText(error.code).toLowerCase(),
            identityResolution: error.detail?.identity_resolution ?? null,
        };
    }
    const rawMessage = error instanceof Error ? error.message : normalizeText(error);
    const match = rawMessage.match(CLOUD_RUN_ERROR_PATTERN);
    if (!match) {
        return {
            statusCode: null,
            code: '',
            identityResolution: null,
        };
    }

    const statusCode = Number(match[1]);
    const payloadText = normalizeText(match[2]);

    let parsedPayload = null;
    try {
        parsedPayload = payloadText ? JSON.parse(payloadText) : null;
    } catch {
        parsedPayload = null;
    }

    const detail = parsedPayload && typeof parsedPayload === 'object' && 'detail' in parsedPayload
        ? parsedPayload.detail
        : parsedPayload;
    const normalizedDetail = detail && typeof detail === 'object' ? detail : null;
    const code = normalizeText(normalizedDetail?.code).toLowerCase();
    const identityResolution = normalizedDetail?.identity_resolution && typeof normalizedDetail.identity_resolution === 'object'
        ? normalizedDetail.identity_resolution
        : normalizedDetail;

    return {
        statusCode: Number.isFinite(statusCode) ? statusCode : null,
        code,
        status: normalizeText(normalizedDetail?.status),
        identityIndex: normalizeText(normalizedDetail?.identity_index),
        identityResolution,
    };
};

// Every security left out of the risk matrix is only waiting for market data.
const isMarketDataPendingServingDecision = (servingDecision) => {
    const droppedByReason = servingDecision?.dropped?.dropped_count_by_reason;
    if (!droppedByReason || typeof droppedByReason !== 'object') {
        return false;
    }
    const pendingCount = Number(droppedByReason.market_data_pending || 0);
    const hasOtherDroppedReason = Object.entries(droppedByReason).some(([reason, count]) => (
        reason !== 'market_data_pending' && Number(count || 0) > 0
    ));
    return pendingCount > 0 && !hasOtherDroppedReason;
};

const buildIdentityResolutionWarning = ({
    identityResolution = null,
    legacyCode = '',
} = {}) => {
    const resolvedCount = normalizeCount(identityResolution?.resolved_count);
    const codeLabel = normalizeText(legacyCode || identityResolution?.code);
    const actionableAmbiguityCount = countActionableOutboundAmbiguities(identityResolution);
    const droppedCount = normalizeCount(identityResolution?.dropped_count);
    const unresolvedCount = normalizeCount(identityResolution?.unresolved_count);
    const ambiguousCount = normalizeCount(identityResolution?.ambiguous_count);
    const unresolvedOrAmbiguousCount = [unresolvedCount, ambiguousCount]
        .filter((count) => count != null && count > 0)
        .reduce((total, count) => total + count, 0);
    const issueCount = [
        droppedCount,
        unresolvedOrAmbiguousCount > 0 ? unresolvedOrAmbiguousCount : null,
        actionableAmbiguityCount,
    ].find((count) => count != null && count > 0);
    const noResolved = (resolvedCount != null && resolvedCount === 0)
        || codeLabel === 'identity_unresolved_all';

    if (noResolved) {
        return 'No securities could be resolved. Open Security Resolution to fix identifiers.';
    }
    if (issueCount != null && issueCount > 0) {
        return `${issueCount} securit${issueCount === 1 ? 'y' : 'ies'} ${issueCount === 1 ? 'needs' : 'need'} Security Resolution. Showing available risk data for the rest.`;
    }
    return 'Some securities need Security Resolution. Showing available risk data for the rest.';
};

export default function useFactorMatrixWorkflow({
    userUid,
    selectedFactorModel,
    selectedBetaSource,
    requestedFactorDataModel,
    watchlistIsReady,
    factorSnapshotDate,
    factorDataModel,
    optimizerIdioVolSource,
    matrixLoadRequestIdRef,
    setMatricesLoading,
    setFactorSnapshotDate,
    setFactorDataModel,
    setFactorNames,
    setImputedWatchlistCogniveIds,
    setIgnoredWatchlistCogniveIds,
    clearFactorMatrixState,
    clearRiskDerivedState,
    setError,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarOpen,
}) {
    const [outboundResolutionDialogOpen, setOutboundResolutionDialogOpen] = useState(false);
    const [outboundResolutionIdentityResolution, setOutboundResolutionIdentityResolution] = useState(null);
    // Factor-matrix availability is a separate concern from watchlist
    // existence. `factorMatricesUnavailable=true` means the backend returned
    // an expected "no matrices for this scope" signal (empty coverage / no
    // identifiers on the selected factor model). It never implies the
    // watchlist is empty — durable watchlist rows in Postgres are the
    // authoritative source for that, surfaced via applyIndustryRows in
    // usePortfolioBaseDataFetchers.
    const [factorMatricesUnavailable, setFactorMatricesUnavailable] = useState(false);
    const [factorContextUnavailable, setFactorContextUnavailable] = useState(false);
    const [factorDataRecoveryRequired, setFactorDataRecoveryRequired] = useState(false);
    const { setAiIdioVolStatus } = useAppContext();

    const syncOutboundResolutionDialog = useCallback((identityResolution) => {
        const actionableCount = countActionableOutboundAmbiguities(identityResolution);
        if (actionableCount > 0) {
            setOutboundResolutionIdentityResolution(identityResolution);
            setOutboundResolutionDialogOpen(true);
            return;
        }
        setOutboundResolutionDialogOpen(false);
        setOutboundResolutionIdentityResolution(null);
    }, []);

    const closeOutboundResolutionDialog = useCallback(() => {
        setOutboundResolutionDialogOpen(false);
    }, []);

    // Pure query used only to decide whether to fetch factor matrices when
    // the caller doesn't yet know watchlist readiness. It intentionally does
    // NOT write to any watchlist state — usePortfolioBaseDataFetchers'
    // applyIndustryRows is the single owner of watchlistIsReady, driven by
    // the durable Postgres/watchlist rows.
    const probeWatchlistReadinessForMatrices = useCallback(async ({
        includeWaiting = false,
        betaSource = selectedBetaSource,
        throwRequestErrors = false,
    } = {}) => {
        if (!userUid) {
            return { state: WATCHLIST_MATRIX_PROBE_STATES.UNAVAILABLE };
        }

        try {
            const watchlistResp = await callBackend('fetch_watchlist_info', 'POST', {
                beta_source: betaSource,
                factorModel: selectedFactorModel,
                factor_snapshot_date: factorSnapshotDate,
                factor_data_model: factorDataModel ?? requestedFactorDataModel,
                include_waiting_market_data: Boolean(includeWaiting),
                readiness_only: true,
            });
            const state = watchlistResp?.matrix_readiness?.state;
            if (watchlistResp?.success !== true || ![
                WATCHLIST_MATRIX_PROBE_STATES.EMPTY,
                WATCHLIST_MATRIX_PROBE_STATES.READY,
                WATCHLIST_MATRIX_PROBE_STATES.PENDING,
            ].includes(state)) {
                return { state: WATCHLIST_MATRIX_PROBE_STATES.UNAVAILABLE };
            }
            return { state };
        } catch (error) {
            if (throwRequestErrors) throw error;
            console.error('Error checking watchlist readiness before loading matrices:', error);
            return { state: WATCHLIST_MATRIX_PROBE_STATES.UNAVAILABLE };
        }
    }, [
        factorDataModel,
        factorSnapshotDate,
        requestedFactorDataModel,
        selectedBetaSource,
        selectedFactorModel,
        userUid,
    ]);

    const syncAiIdioVolStatus = useCallback(({
        idioVolSource,
        matricesResponse,
        factorModel = selectedFactorModel,
        fallbackFactorDataModel = factorDataModel ?? requestedFactorDataModel,
        fallbackFactorSnapshotDate = factorSnapshotDate,
    } = {}) => {
        if (!isAiIdioVolSource(idioVolSource) || !matricesResponse) {
            setAiIdioVolStatus(null);
            return;
        }

        setAiIdioVolStatus({
            factorModel: normalizeUserFactorModel(factorModel),
            factorDataModel: matricesResponse?.factor_data_model ?? fallbackFactorDataModel ?? null,
            factorSnapshotDate: matricesResponse?.factor_snapshot_date ?? fallbackFactorSnapshotDate ?? null,
            idioVolResolution: matricesResponse?.idio_vol_resolution ?? null,
            fallbackSummary: matricesResponse?.fallback_summary ?? null,
            aiModelMetadata: matricesResponse?.ai_model_metadata ?? null,
        });
    }, [
        factorDataModel,
        factorSnapshotDate,
        requestedFactorDataModel,
        selectedFactorModel,
        setAiIdioVolStatus,
    ]);

    const fetchResolvedFactorMatrices = useCallback(async ({
        riskSource = selectedFactorModel,
        factorDataModelOverride = requestedFactorDataModel,
        factorSnapshotDateOverride = factorSnapshotDate,
        idioVolSource = optimizerIdioVolSource,
        updateAiStatus = false,
    } = {}) => {
        if (!userUid || !riskSource) {
            return null;
        }

        const payload = {
            risk_source: riskSource,
            factor_data_model: factorDataModelOverride,
            idio_vol_source: normalizeIdioVolSource(idioVolSource),
            include_matrices: false,
        };
        if (factorSnapshotDateOverride) {
            payload.factor_snapshot_date = factorSnapshotDateOverride;
        }
        const response = await callBackend('get_factor_matrices', 'POST', payload);
        if (updateAiStatus) {
            syncAiIdioVolStatus({
                idioVolSource,
                matricesResponse: response,
                factorModel: riskSource,
                fallbackFactorDataModel: factorDataModelOverride,
                fallbackFactorSnapshotDate: factorSnapshotDateOverride,
            });
        }
        return response;
    }, [
        factorSnapshotDate,
        optimizerIdioVolSource,
        requestedFactorDataModel,
        selectedFactorModel,
        syncAiIdioVolStatus,
        userUid,
    ]);

    const fetchFactorMatrices = useCallback(
        async ({
            idioVolSource = optimizerIdioVolSource,
            betaSource = selectedBetaSource,
            forceWatchlistProbe = false,
            useLatestFactorSnapshot = false,
            factorVendorOverride,
            factorDataModelOverride,
            factorSnapshotDateOverride,
            preserveExistingOnMatrixFailure = false,
            stagePublicationState = null,
            stagePresentationState = null,
            isCurrentRequest = null,
        } = {}) => {
            const factorVendor = factorVendorOverride ?? selectedFactorModel;
            if (!userUid || !factorVendor) {
                return formatMatrixLoadResult({
                    matrixLoadStatus: 'not_requested',
                });
            }

            const requestId = ++matrixLoadRequestIdRef.current;
            const externalIsCurrent = typeof isCurrentRequest === 'function'
                ? isCurrentRequest
                : () => true;
            const isCurrentMatrixRequest = () => (
                matrixLoadRequestIdRef.current === requestId && externalIsCurrent()
            );
            const present = (apply) => {
                if (!stagePresentationState) apply();
            };
            const publishMatrixState = (apply) => {
                if (typeof stagePublicationState === 'function') {
                    stagePublicationState(apply, isCurrentMatrixRequest);
                } else if (isCurrentMatrixRequest()) {
                    apply();
                }
            };
            if (!externalIsCurrent()) {
                return formatMatrixLoadResult({
                    matrixLoadStatus: 'cancelled',
                });
            }
            // A cached `false` only means the UI has no matrix-ready rows. It
            // cannot distinguish a durable empty universe from non-empty rows
            // that are still resolving, so only cached `true` is authoritative
            // enough to skip the durable probe.
            const watchlistProbe = forceWatchlistProbe || watchlistIsReady !== true
                ? await probeWatchlistReadinessForMatrices({
                    includeWaiting: forceWatchlistProbe,
                    betaSource,
                    throwRequestErrors: preserveExistingOnMatrixFailure
                        && typeof stagePublicationState === 'function'
                        && typeof stagePresentationState === 'function',
                })
                : { state: WATCHLIST_MATRIX_PROBE_STATES.READY };
            if (!isCurrentMatrixRequest()) {
                return formatMatrixLoadResult({
                    matrixLoadStatus: 'cancelled',
                });
            }
            const clearUnavailableMatrixState = ({ authoritativeEmpty = false } = {}) => {
                if (preserveExistingOnMatrixFailure && !authoritativeEmpty) {
                    return;
                }
                const clearState = () => {
                    clearFactorMatrixState();
                    clearRiskDerivedState();
                    setAiIdioVolStatus(null);
                };
                if (authoritativeEmpty) {
                    publishMatrixState(() => {
                        if (stagePresentationState) {
                            setFactorContextUnavailable(false);
                            setFactorDataRecoveryRequired(false);
                            setMatricesLoading(false);
                        }
                        clearState();
                    });
                } else {
                    present(clearState);
                }
            };
            const markFactorMatricesUnavailable = () => {
                if (!preserveExistingOnMatrixFailure) {
                    present(() => setFactorMatricesUnavailable(true));
                }
            };
            if (watchlistProbe.state === WATCHLIST_MATRIX_PROBE_STATES.EMPTY) {
                present(() => setFactorContextUnavailable(false));
                present(() => setFactorDataRecoveryRequired(false));
                clearUnavailableMatrixState({ authoritativeEmpty: true });
                present(() => setMatricesLoading(false));
                return formatMatrixLoadResult({
                    matrixLoadStatus: 'empty_watchlist',
                    matrixLoadReason: 'no_ready_watchlist_rows',
                });
            }
            if (watchlistProbe.state !== WATCHLIST_MATRIX_PROBE_STATES.READY) {
                present(() => setFactorContextUnavailable(false));
                clearUnavailableMatrixState();
                present(() => setMatricesLoading(false));
                return formatMatrixLoadResult({
                    matrixLoadStatus: watchlistProbe.state === WATCHLIST_MATRIX_PROBE_STATES.PENDING
                        ? 'pending'
                        : 'unavailable',
                    matrixLoadReason: watchlistProbe.state === WATCHLIST_MATRIX_PROBE_STATES.PENDING
                        ? 'watchlist_rows_not_ready'
                        : 'watchlist_read_unavailable',
                });
            }

            present(() => setFactorContextUnavailable(false));
            present(() => setMatricesLoading(true));

            try {
                const res = await fetchResolvedFactorMatrices({
                    riskSource: factorVendor,
                    factorDataModelOverride: factorDataModelOverride ?? requestedFactorDataModel,
                    factorSnapshotDateOverride: factorSnapshotDateOverride !== undefined
                        ? factorSnapshotDateOverride
                        : (useLatestFactorSnapshot ? null : factorSnapshotDate),
                    idioVolSource,
                    updateAiStatus: false,
                });
                if (!isCurrentMatrixRequest()) {
                    return formatMatrixLoadResult({
                        matrixLoadStatus: 'cancelled',
                    });
                }
                if (res?.status === NOTHING_ELIGIBLE_STATUS) {
                    // A normal answer, not a failure. Securities that only wait
                    // for market data are a passing state; otherwise the factor
                    // columns stay blank until coverage arrives.
                    if (isMarketDataPendingServingDecision(res?.serving_decision)) {
                        present(() => setError(''));
                        return formatMatrixLoadResult({
                            matrixLoadStatus: 'pending',
                            matrixLoadReason: 'market_data_pending',
                        });
                    }
                    clearUnavailableMatrixState();
                    markFactorMatricesUnavailable();
                    return formatMatrixLoadResult({
                        matrixLoadStatus: 'unavailable',
                        matrixLoadReason: 'no_eligible_securities',
                    });
                }

                const responseStatus = res?.status;
                const identityResolution = res?.identity_resolution;
                const resolvedCount = normalizeCount(identityResolution?.resolved_count);
                const coverage = res?.coverage && typeof res.coverage === 'object'
                    ? res.coverage
                    : null;
                const coverageCount = normalizeCount(
                    coverage?.resolved_count ??
                    coverage?.covered_count ??
                    coverage?.row_count,
                );
                const hasResolvedRows = Boolean(
                    (resolvedCount != null && resolvedCount > 0) ||
                    (coverageCount != null && coverageCount > 0) ||
                    res?.success === true,
                );
                // Unresolved securities are shown in the Watchlist and Security
                // Resolution, so a partly resolved load clears the error quietly.
                if (
                    isStartupIdentityNonFatal({
                        status: responseStatus,
                        identityResolution,
                        hasResolvedRows,
                    })
                ) {
                    present(() => setError(''));
                }
                const normalizeCogniveIds = (values = []) => [...new Set(
                    (Array.isArray(values) ? values : [])
                        .map((value) => String(value || '').trim())
                        .filter(Boolean),
                )];
                const nextImputedCogniveIds = normalizeCogniveIds(
                    res?.coverage?.imputed_cognive_ids ?? [],
                );
                const nextIgnoredCogniveIds = normalizeCogniveIds(
                    res?.coverage?.ignored_cognive_ids ?? [],
                );
                if (res?.success === false) {
                    present(() => syncOutboundResolutionDialog(identityResolution));
                    clearUnavailableMatrixState();
                    markFactorMatricesUnavailable();
                    return formatMatrixLoadResult({
                        response: null,
                        matrixLoadStatus: 'unavailable',
                        matrixLoadReason: 'matrix_metadata_unavailable',
                    });
                }

                publishMatrixState(() => {
                    if (stagePresentationState) {
                        setFactorContextUnavailable(false);
                        setMatricesLoading(false);
                    }
                    syncAiIdioVolStatus({
                        idioVolSource,
                        matricesResponse: res,
                        factorModel: factorVendor,
                        fallbackFactorDataModel: factorDataModelOverride ?? requestedFactorDataModel,
                        fallbackFactorSnapshotDate: factorSnapshotDateOverride !== undefined
                            ? factorSnapshotDateOverride
                            : factorSnapshotDate,
                    });
                    syncOutboundResolutionDialog(identityResolution);
                    setFactorSnapshotDate(res?.factor_snapshot_date ?? null);
                    setFactorDataModel(
                        res?.factor_data_model ??
                        factorDataModelOverride ??
                        requestedFactorDataModel ??
                        null,
                    );
                    setFactorNames(
                        Array.isArray(res?.factor_names)
                            ? res.factor_names
                            : flattenFactorGroups(res?.factor_groups, res?.factor_group_order),
                    );
                    setImputedWatchlistCogniveIds(nextImputedCogniveIds);
                    setIgnoredWatchlistCogniveIds(nextIgnoredCogniveIds);
                    setFactorMatricesUnavailable(false);
                    setFactorDataRecoveryRequired(false);
                });
                return formatMatrixLoadResult({
                    response: res,
                    matrixLoadStatus: 'ready',
                });
            } catch (err) {
                if (!isCurrentMatrixRequest()) {
                    return formatMatrixLoadResult({
                        matrixLoadStatus: 'cancelled',
                    });
                }
                const parsedError = parseCloudRunError(err);
                clearUnavailableMatrixState();
                if (
                    parsedError.statusCode === 503
                    && parsedError.code === WATCHLIST_FACTOR_SERVING_DATA_DRIFT
                ) {
                    markFactorMatricesUnavailable();
                    present(() => setFactorDataRecoveryRequired(true));
                    return formatMatrixLoadResult({
                        matrixLoadStatus: 'unavailable',
                        matrixLoadReason: WATCHLIST_FACTOR_SERVING_DATA_DRIFT,
                    });
                }
                if (
                    parsedError.statusCode === 503
                    && parsedError.code === SERVING_MANIFEST_CONTEXT_UNAVAILABLE
                ) {
                    markFactorMatricesUnavailable();
                    present(() => setFactorContextUnavailable(true));
                    return formatMatrixLoadResult({
                        matrixLoadStatus: 'unavailable',
                        matrixLoadReason: SERVING_MANIFEST_CONTEXT_UNAVAILABLE,
                    });
                }
                if (isExpectedNoWatchlistMatrixError(err)) {
                    // The backend can return this signal for real reasons
                    // that are NOT "watchlist is missing" — e.g. the chosen
                    // factor model has no matrix coverage for these
                    // identifiers, or the snapshot date has no data. Do not
                    // touch watchlistIsReady here; watchlist existence is
                    // owned by usePortfolioBaseDataFetchers. Signal factor
                    // unavailability so the UI can render blank factor
                    // columns without treating the watchlist as gone.
                    markFactorMatricesUnavailable();
                    return formatMatrixLoadResult({
                        matrixLoadStatus: 'unavailable',
                        matrixLoadReason: 'expected_no_watchlist_matrix',
                    });
                }
                if (
                    parsedError.statusCode === 409
                    && LEGACY_IDENTITY_CODES.has(parsedError.code)
                ) {
                    present(() => syncOutboundResolutionDialog(parsedError.identityResolution));
                    const normalizedStatus = normalizeStartupIdentityStatus({
                        status: parsedError.status,
                        identityResolution: parsedError.identityResolution,
                        hasResolvedRows: false,
                    });
                    present(() => setError(''));
                    present(() => setSnackbarMessage(
                        buildIdentityResolutionWarning({
                            identityResolution: parsedError.identityResolution,
                            legacyCode: parsedError.code || normalizedStatus,
                        }),
                    ));
                    present(() => setSnackbarSeverity('warning'));
                    present(() => setSnackbarOpen(true));
                    return formatMatrixLoadResult({
                        matrixLoadStatus: 'unavailable',
                        matrixLoadReason: parsedError.code || normalizedStatus,
                    });
                }
                // Required held publication keeps the original failed request;
                // expected coverage/lineage dispositions above remain strict results.
                if (preserveExistingOnMatrixFailure
                    && typeof stagePublicationState === 'function'
                    && typeof stagePresentationState === 'function') throw err;
                console.error('Failed to load factor matrices:', err);
                present(() => setError('Failed to load factor matrices.'));
                present(() => setSnackbarMessage('Failed to load factor matrices.'));
                present(() => setSnackbarSeverity('error'));
                present(() => setSnackbarOpen(true));
                return formatMatrixLoadResult({
                    matrixLoadStatus: 'unavailable',
                    matrixLoadReason: 'matrix_fetch_failed',
                });
            } finally {
                if (matrixLoadRequestIdRef.current === requestId) {
                    present(() => setMatricesLoading(false));
                }
            }
        },
        [
            clearFactorMatrixState,
            clearRiskDerivedState,
            factorSnapshotDate,
            fetchResolvedFactorMatrices,
            matrixLoadRequestIdRef,
            optimizerIdioVolSource,
            probeWatchlistReadinessForMatrices,
            requestedFactorDataModel,
            selectedBetaSource,
            selectedFactorModel,
            setAiIdioVolStatus,
            setError,
            setFactorDataModel,
            setFactorNames,
            setFactorSnapshotDate,
            setIgnoredWatchlistCogniveIds,
            setImputedWatchlistCogniveIds,
            setMatricesLoading,
            setSnackbarMessage,
            setSnackbarOpen,
            setSnackbarSeverity,
            syncAiIdioVolStatus,
            syncOutboundResolutionDialog,
            userUid,
            watchlistIsReady,
        ],
    );

    const buildFactorContextFromResponse = useCallback((matricesResponse, overrides = {}) => {
        if (!matricesResponse || typeof matricesResponse !== 'object') {
            return null;
        }
        return buildFactorContextFromMatrixMetadata({
            response: matricesResponse,
            factorModel: overrides.factorModel ?? selectedFactorModel,
            factorDataModel:
                overrides.factorDataModel ??
                matricesResponse?.factor_data_model ??
                factorDataModel ??
                requestedFactorDataModel,
            factorSnapshotDate:
                overrides.factorSnapshotDate ??
                matricesResponse?.factor_snapshot_date ??
                factorSnapshotDate,
            betaSource: overrides.betaSource ?? selectedBetaSource,
            idioVolSource: overrides.idioVolSource ?? optimizerIdioVolSource,
        });
    }, [
        factorDataModel,
        factorSnapshotDate,
        optimizerIdioVolSource,
        requestedFactorDataModel,
        selectedBetaSource,
        selectedFactorModel,
    ]);

    const getCurrentFactorContext = useCallback(() => buildFactorContextFromMatrixMetadata({
        factorModel: selectedFactorModel,
        factorDataModel: factorDataModel ?? requestedFactorDataModel,
        factorSnapshotDate,
        betaSource: selectedBetaSource,
        idioVolSource: optimizerIdioVolSource,
    }), [
        factorDataModel,
        factorSnapshotDate,
        optimizerIdioVolSource,
        requestedFactorDataModel,
        selectedBetaSource,
        selectedFactorModel,
    ]);

    const retryMatricesAfterOutboundChoice = useCallback(async () => {
        return await fetchFactorMatrices();
    }, [fetchFactorMatrices]);

    return {
        fetchFactorMatrices,
        factorContextUnavailable,
        factorDataRecoveryRequired,
        factorMatricesUnavailable,
        buildFactorContextFromResponse,
        getCurrentFactorContext,
        outboundResolutionDialogOpen,
        outboundResolutionIdentityResolution,
        closeOutboundResolutionDialog,
        retryMatricesAfterOutboundChoice,
    };
}
